import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "./Navbar";
import Footer from "./Footer";
import Donate from "./Donate";
import Contact from "./Contact";
import { useSeo } from "../hooks/useSeo";

const Gallery = () => {
  useSeo({
    title: "Photo Gallery - PFA Movement",
    description:
      "Explore our gallery showcasing PFA Movement's events, activities, and community impact in Afghanistan.",
    image: "/images/gallery/og-image.jpg",
  });

  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [photosPerPage] = useState(12);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalCurrentSlide, setModalCurrentSlide] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState({});
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Generate comprehensive Image Schema for SEO
  const generateImageStructuredData = () => {
    const baseUrl = window.location.origin;

    const imageObjects = photoData.map((photo, index) => ({
      "@type": "ImageObject",
      position: index + 1,
      name: photo.title,
      description: photo.description,
      contentUrl: `${baseUrl}${photo.image}`,
      thumbnailUrl: `${baseUrl}${photo.image}`,
      license: "https://creativecommons.org/licenses/by-nc/4.0/",
      acquireLicensePage: `${baseUrl}/gallery`,
      creator: {
        "@type": "Organization",
        name: "Progressive Forces of Afghanistan Movement",
        url: "https://pfa-movement.com",
      },
      copyrightNotice: `Copyright © ${new Date().getFullYear()} Progressive Forces of Afghanistan Movement`,
      creditText: "Progressive Forces of Afghanistan Movement",
      caption: photo.title,
      representativeOfPage: index < 5 ? "True" : "False",
      width: photo.width.toString(),
      height: photo.height.toString(),
    }));

    return {
      "@context": "https://schema.org",
      "@type": "ImageGallery",
      name: "PFA Movement Gallery - Women's Rights Activism in Afghanistan",
      description:
        "Photo documentation of Progressive Forces of Afghanistan Movement's fight for women's rights, education, and gender equality against Taliban oppression. Includes protests, conferences, and activism events worldwide.",
      url: `${baseUrl}/gallery`,
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: photoData.length,
        itemListElement: imageObjects.map((image, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: image,
        })),
      },
      publisher: {
        "@type": "Organization",
        name: "Progressive Forces of Afghanistan Movement",
        logo: {
          "@type": "ImageObject",
          url: `${baseUrl}/logo-512.png`,
          width: "512",
          height: "512",
        },
      },
      datePublished: "2023-01-01",
      dateModified: new Date().toISOString().split("T")[0],
      keywords: [
        "Afghanistan women rights",
        "Taliban protest",
        "women education Afghanistan",
        "gender equality",
        "Afghan women activism",
        "PFA Movement gallery",
        "women rights protest",
        "Afghanistan democracy",
      ],
      about: {
        "@type": "Thing",
        name: "Women's Rights in Afghanistan",
      },
      genre: ["Photo Journalism", "Activism Documentation", "Human Rights"],
      material: "Digital photographs",
      isFamilyFriendly: true,
      inLanguage: ["en", "fa", "de"],
    };
  };

  // Generate Video Schema for SEO
  const generateVideoStructuredData = () => {
    const baseUrl = window.location.origin;

    const videoObjects = videoData.map((video, index) => ({
      "@type": "VideoObject",
      position: index + 1,
      name: video.title,
      description: video.description,
      thumbnailUrl: video.thumbnail,
      contentUrl: `https://www.youtube.com/watch?v=${video.youtubeId}`,
      embedUrl: `https://www.youtube.com/embed/${video.youtubeId}`,
      uploadDate: "2023-01-01",
      duration: "PT5M",
      publisher: {
        "@type": "Organization",
        name: "Progressive Forces of Afghanistan Movement",
        logo: {
          "@type": "ImageObject",
          url: `${baseUrl}/logo-512.png`,
        },
      },
      creator: {
        "@type": "Organization",
        name: "Progressive Forces of Afghanistan Movement",
      },
    }));

    return {
      "@context": "https://schema.org",
      "@type": "VideoGallery",
      name: "PFA Movement Videos - Afghanistan Women's Rights Activism",
      description:
        "Video documentation of protests, speeches, and events by Progressive Forces of Afghanistan Movement fighting for women's rights and gender equality.",
      url: `${baseUrl}/gallery`,
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: videoData.length,
        itemListElement: videoObjects.map((video, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: video,
        })),
      },
    };
  };

  // Photo data with SEO-optimized alt texts
  const photoData = [
    // ... previous photos (tagder1-5)

    // New Neshast Zanan photos
    {
      id: 56,
      title: t("gallery.photos.neshast_zanan.title"),
      description: t("gallery.photos.neshast_zanan.description"),
      image: "/images/gallery/hamburg/neshastZanan1.jpg",
      category: "Event",
      alt: "Panel discussion on women's rights in Afghanistan held in Hamburg",
      width: 800,
      height: 600,
    },
    {
      id: 57,
      title: t("gallery.photos.neshast_zanan.title"),
      description: t("gallery.photos.neshast_zanan.description"),
      image: "/images/gallery/hamburg/neshastZanan2.jpg",
      category: "Event",
      alt: "International experts gather to address Afghanistan's humanitarian crisis",
      width: 800,
      height: 600,
    },
    {
      id: 58,
      title: t("gallery.photos.neshast_zanan.title"),
      description: t("gallery.photos.neshast_zanan.description"),
      image: "/images/gallery/hamburg/neshastZanan3.jpg",
      category: "Event",
      alt: "German Parliament member speaks on human rights at Hamburg panel",
      width: 800,
      height: 600,
    },
    {
      id: 59,
      title: t("gallery.photos.neshast_zanan.title"),
      description: t("gallery.photos.neshast_zanan.description"),
      image: "/images/gallery/hamburg/neshastZanan4.jpg",
      category: "Event",
      alt: "Afghan women leaders share their stories at Hamburg forum",
      width: 800,
      height: 600,
    },
    {
      id: 60,
      title: t("gallery.photos.neshast_zanan.title"),
      description: t("gallery.photos.neshast_zanan.description"),
      image: "/images/gallery/hamburg/neshastZanan5.jpg",
      category: "Event",
      alt: "Three specialized panels examine Taliban rule and its consequences",
      width: 800,
      height: 600,
    },
    {
      id: 61,
      title: t("gallery.photos.neshast_zanan.title"),
      description: t("gallery.photos.neshast_zanan.description"),
      image: "/images/gallery/hamburg/neshastZanan6.jpg",
      category: "Event",
      alt: "Civil society and political leaders unite for Afghanistan in Hamburg",
      width: 800,
      height: 600,
    },
    {
      id: 62,
      title: t("gallery.photos.neshast_zanan.title"),
      description: t("gallery.photos.neshast_zanan.description"),
      image: "/images/gallery/hamburg/neshastZanan7.jpg",
      category: "Event",
      alt: "Hamburg panel concludes with call for continued global solidarity",
      width: 800,
      height: 600,
    },
    {
      id: 63,
      title: t("gallery.photos.neshast_zanan.title"),
      description: t("gallery.photos.neshast_zanan.description"),
      image: "/images/gallery/hamburg/neshastZanan8.jpg",
      category: "Event",
      alt: "Hamburg panel concludes with call for continued global solidarity",
      width: 800,
      height: 600,
    },
    {
      id: 51,
      title: t("gallery.photos.tagder.title"),
      description: t("gallery.photos.tagder.description"),
      image: "/images/gallery/tagder1.jpeg",
      category: "Event",
      alt: "Afghan cultural table in Leer",
      width: 800,
      height: 600,
    },
    {
      id: 52,
      title: t("gallery.photos.tagder.title"),
      description: t("gallery.photos.tagder.description"),
      image: "/images/gallery/tagder2.jpeg",
      category: "Event",
      alt: "Welcoming community in Leer",
      width: 800,
      height: 600,
    },
    {
      id: 53,
      title: t("gallery.photos.tagder.title"),
      description: t("gallery.photos.tagder.description"),
      image: "/images/gallery/tagder3.jpeg",
      category: "Event",
      alt: "Mawloda Tawana leading Afghan cultural showcase",
      width: 800,
      height: 600,
    },
    {
      id: 54,
      title: t("gallery.photos.tagder.title"),
      description: t("gallery.photos.tagder.description"),
      image: "/images/gallery/tagder4.jpeg",
      category: "Event",
      alt: "Celebrating diversity in Leer",
      width: 800,
      height: 600,
    },
    {
      id: 55,
      title: t("gallery.photos.tagder.title"),
      description: t("gallery.photos.tagder.description"),
      image: "/images/gallery/tagder5.jpeg",
      category: "Event",
      alt: "Official invitation poster",
      width: 800,
      height: 600,
    },
    {
      id: 51,
      title: t("gallery.photos.hamburg.title"),
      description: t("gallery.photos.hamburg.description"),
      image: "/images/gallery/hamburg/ha1.jpg", // Suggested path; please verify and update.
      category: "Event",
      alt: "Hamburg Protest",
      width: 800,
      height: 600,
    },
    {
      id: 52,
      title: t("gallery.photos.hamburg.title"),
      description: t("gallery.photos.hamburg.description"),
      image: "/images/gallery/hamburg/ha2.jpg", // Suggested path; please verify and update.
      category: "Event",
      alt: "Hamburg Protest",
      width: 800,
      height: 600,
    },
    {
      id: 53,
      title: t("gallery.photos.hamburg.title"),
      description: t("gallery.photos.hamburg.description"),
      image: "/images/gallery/hamburg/ha3.jpg", // Suggested path; please verify and update.
      category: "Event",
      alt: "Hamburg Protest",
      width: 800,
      height: 600,
    },
    {
      id: 54,
      title: t("gallery.photos.hamburg.title"),
      description: t("gallery.photos.hamburg.description"),
      image: "/images/gallery/hamburg/ha4.jpg", // Suggested path; please verify and update.
      category: "Event",
      alt: "Hamburg Protest",
      width: 800,
      height: 600,
    },
    {
      id: 55,
      title: t("gallery.photos.hamburg.title"),
      description: t("gallery.photos.hamburg.description"),
      image: "/images/gallery/hamburg/ha5.jpg", // Suggested path; please verify and update.
      category: "Event",
      alt: "Hamburg Protest",
      width: 800,
      height: 600,
    },
    {
      id: 56,
      title: t("gallery.photos.hamburg.title"),
      description: t("gallery.photos.hamburg.description"),
      image: "/images/gallery/hamburg/ha6.jpg", // Suggested path; please verify and update.
      category: "Event",
      alt: "Hamburg Protest",
      width: 800,
      height: 600,
    },
    {
      id: 57,
      title: t("gallery.photos.hamburg.title"),
      description: t("gallery.photos.hamburg.description"),
      image: "/images/gallery/hamburg/ha7.jpg", // Suggested path; please verify and update.
      category: "Event",
      alt: "Hamburg Protest",
      width: 800,
      height: 600,
    },
    {
      id: 58,
      title: t("gallery.photos.hamburg.title"),
      description: t("gallery.photos.hamburg.description"),
      image: "/images/gallery/hamburg/ha8.jpg", // Suggested path; please verify and update.
      category: "Event",
      alt: "Hamburg Protest",
      width: 800,
      height: 600,
    },
    {
      id: 59,
      title: t("gallery.photos.hamburg.title"),
      description: t("gallery.photos.hamburg.description"),
      image: "/images/gallery/hamburg/ha9.jpg", // Suggested path; please verify and update.
      category: "Event",
      alt: "Hamburg Protest",
      width: 800,
      height: 600,
    },
    {
      id: 60,
      title: t("gallery.photos.hamburg.title"),
      description: t("gallery.photos.hamburg.description"),
      image: "/images/gallery/hamburg/ha10.jpg", // Suggested path; please verify and update.
      category: "Event",
      alt: "Hamburg Protest",
      width: 800,
      height: 600,
    },
    {
      id: 50,
      title: t("gallery.photos.maec_room_dedication.title"),
      description: t("gallery.photos.maec_room_dedication.description"),
      image: "/images/gallery/hearus3_room_dedication.jpeg", // Suggested path; please verify and update.
      category: "Event",
      alt: "Minister José Manuel Albares announces the naming of a room at the Spanish Ministry of Foreign Affairs in honor of the women and girls of Afghanistan.",
      width: 800,
      height: 600,
    },
    {
      id: 49, // Assuming 44 was the last used ID. Adjust sequentially.
      title: t("gallery.photos.richard_bennett.title"),
      description: t("gallery.photos.richard_bennett.description"),
      image: "/images/gallery/richard_bennett_un_rapporteur.jpeg", // Suggested path; please verify and update.
      category: "Expert", // Suggested category; adjust based on your taxonomy.
      alt: "Richard Bennett, UN Special Rapporteur on the human rights situation in Afghanistan",
      width: 800,
      height: 600,
    },
    {
      id: 51,
      title: t("gallery.photos.mawloda_tawana_hearus.title"),
      description: t("gallery.photos.mawloda_tawana_hearus.description"),
      image: "/images/gallery/hearus1.jpeg", // Adjust path/filename
      category: "Event",
      alt: "Mawloda Tawana, Founder of Progressive Forces of Afghanistan (PFA) Movement, speaking at the HearUs International Forum.",
      width: 800,
      height: 600,
    },
    {
      id: 52,
      title: t("gallery.photos.mawloda_tawana_hearus.title"),
      description: t("gallery.photos.mawloda_tawana_hearus.description"),
      image: "/images/gallery/hearus2.jpeg", // Adjust path/filename
      category: "Event",
      alt: "Mawloda Tawana, Founder of Progressive Forces of Afghanistan (PFA) Movement, speaking at the HearUs International Forum.",
      width: 800,
      height: 600,
    },
    {
      id: 53,
      title: t("gallery.photos.mawloda_tawana_hearus.title"),
      description: t("gallery.photos.mawloda_tawana_hearus.description"),
      image: "/images/gallery/hearus3.jpeg", // Adjust path/filename
      category: "Event",
      alt: "Mawloda Tawana, Founder of Progressive Forces of Afghanistan (PFA) Movement, speaking at the HearUs International Forum.",
      width: 800,
      height: 600,
    },
    {
      id: 54,
      title: t("gallery.photos.mawloda_tawana_hearus.title"),
      description: t("gallery.photos.mawloda_tawana_hearus.description"),
      image: "/images/gallery/hearus4.jpeg", // Adjust path/filename
      category: "Event",
      alt: "Mawloda Tawana, Founder of Progressive Forces of Afghanistan (PFA) Movement, speaking at the HearUs International Forum.",
      width: 800,
      height: 600,
    },

    {
      id: 44,
      title: t("gallery.photos.quadripartite1.title"),
      description: t("gallery.photos.quadripartite1.description"),
      image: "/images/news/quadripartite_resolution_1.jpg",
      category: "Statement",
      alt: "Quadripartite Resolution: Afghan Women's Movements Declare Our Freedom is Non-Negotiable - November 25, 2025",
      width: 800,
      height: 600,
    },
    {
      id: 45,
      title: t("gallery.photos.quadripartite1.title"),
      description: t("gallery.photos.quadripartite1.description"),
      image: "/images/news/quadripartite_resolution_2.jpg",
      category: "Statement",
      alt: "Quadripartite Resolution: Afghan Women's Movements Declare Our Freedom is Non-Negotiable - November 25, 2025",
      width: 800,
      height: 600,
    },
    {
      id: 46,
      title: t("gallery.photos.quadripartite1.title"),
      description: t("gallery.photos.quadripartite1.description"),
      image: "/images/news/quadripartite_resolution_3.jpg",
      category: "Statement",
      alt: "Quadripartite Resolution: Afghan Women's Movements Declare Our Freedom is Non-Negotiable - November 25, 2025",
      width: 800,
      height: 600,
    },
    {
      id: 47,
      title: t("gallery.photos.quadripartite1.title"),
      description: t("gallery.photos.quadripartite1.description"),
      image: "/images/news/quadripartite_resolution_8.jpg",
      category: "Statement",
      alt: "Quadripartite Resolution: Afghan Women's Movements Declare Our Freedom is Non-Negotiable - November 25, 2025",
      width: 800,
      height: 600,
    },
    {
      id: 48,
      title: t("gallery.photos.quadripartite1.title"),
      description: t("gallery.photos.quadripartite1.description"),
      image: "/images/news/quadripartite_resolution_12.jpg",
      category: "Statement",
      alt: "Quadripartite Resolution: Afghan Women's Movements Declare Our Freedom is Non-Negotiable - November 25, 2025",
      width: 800,
      height: 600,
    },
    {
      id: 43,
      title: t("gallery.photos.berlin1.title"),
      description: t("gallery.photos.berlin1.description"),
      image: "/images/berlin1.jpg",
      category: "Activity",
      alt: "Afghanistan Under Taliban: Berlin Conference Condemns 'Gender Apartheid' - 2025",
      width: 800,
      height: 600,
    },
    {
      id: 44,
      title: t("gallery.photos.berlin1.title"),
      description: t("gallery.photos.berlin1.description"),
      image: "/images/berlin2.jpg",
      category: "Activity",
      alt: "Afghanistan Under Taliban: Berlin Conference Condemns 'Gender Apartheid' - 2025",
      width: 800,
      height: 600,
    },
    {
      id: 45,
      title: t("gallery.photos.berlin1.title"),
      description: t("gallery.photos.berlin1.description"),
      image: "/images/berlin3.jpg",
      category: "Activity",
      alt: "Afghanistan Under Taliban: Berlin Conference Condemns 'Gender Apartheid' - 2025",
      width: 800,
      height: 600,
    },
    {
      id: 1,
      title: t("gallery.photos.ir1.title"),
      description: t("gallery.photos.ir1.description"),
      image: "/images/gallery/ir1.jpeg",
      category: "Activity",
      alt: "PFAM members protest for women's rights in Tehran Iran - Progressive Forces of Afghanistan Movement",
      width: 800,
      height: 600,
    },
    {
      id: 2,
      title: t("gallery.photos.ir2.title"),
      description: t("gallery.photos.ir2.description"),
      image: "/images/gallery/ir2.jpeg",
      category: "Activity",
      alt: "PFAM activists raising hands for justice in Tehran - Afghanistan women rights protest",
      width: 800,
      height: 600,
    },
    {
      id: 3,
      title: t("gallery.photos.kbl1.title"),
      description: t("gallery.photos.kbl1.description"),
      image: "/images/gallery/kbl1.jpeg",
      category: "Activity",
      alt: "PFAM advocacy program Kabul Afghanistan 2024 - Women's rights movement",
      width: 800,
      height: 600,
    },
    {
      id: 4,
      title: t("gallery.photos.pkl.title"),
      description: t("gallery.photos.pkl.description"),
      image: "/images/gallery/pkl1.jpeg",
      category: "Activity",
      alt: "Protest against Taliban in Islamabad Pakistan 2024 - Afghan women rights",
      width: 800,
      height: 600,
    },
    {
      id: 5,
      title: t("gallery.photos.pkl2.title"),
      description: t("gallery.photos.pkl2.description"),
      image: "/images/gallery/pkl2.jpeg",
      category: "Activity",
      alt: "International Women's Day protest Islamabad Pakistan 2023 - Afghan women empowerment",
      width: 800,
      height: 600,
    },
    {
      id: 6,
      title: t("gallery.photos.alb.title"),
      description: t("gallery.photos.alb.description"),
      image: "/images/gallery/alb1.jpeg",
      category: "Activity",
      alt: "Women Empowerment Summit Albania 2024 - PFA Movement international conference",
      width: 800,
      height: 600,
    },
    {
      id: 7,
      title: t("gallery.photos.cnf.title"),
      description: t("gallery.photos.cnf.description"),
      image: "/images/gallery/cnf1.jpeg",
      category: "Activity",
      alt: "WFA Conference Gender Justice Accountability 2024 - UN women rights",
      width: 800,
      height: 600,
    },
    {
      id: 8,
      title: t("gallery.photos.pkl4.title"),
      description: t("gallery.photos.pkl4.description"),
      image: "/images/gallery/pkl4.jpeg",
      category: "Activity",
      alt: "Protest against Taliban misogynistic decrees Pakistan 2024",
      width: 800,
      height: 600,
    },
    {
      id: 9,
      title: t("gallery.photos.pklkh1.title"),
      description: t("gallery.photos.pklkh1.description"),
      image: "/images/gallery/pkl-kh1.jpeg",
      category: "Activity",
      alt: "Elimination of violence against women event Islamabad Pakistan 2023",
      width: 800,
      height: 600,
    },
    {
      id: 10,
      title: t("gallery.photos.pklkh2.title"),
      description: t("gallery.photos.pklkh2.description"),
      image: "/images/gallery/pkl-kh2.jpeg",
      category: "Activity",
      alt: "Stop violence against women protest Pakistan 2023 - PFAM activism",
      width: 800,
      height: 600,
    },
    {
      id: 11,
      title: t("gallery.photos.pklkh3.title"),
      description: t("gallery.photos.pklkh3.description"),
      image: "/images/gallery/pkl-kh3.jpeg",
      category: "Activity",
      alt: "Women rights activists Islamabad Pakistan 2023 - Afghan movement",
      width: 800,
      height: 600,
    },
    {
      id: 12,
      title: t("gallery.photos.mrf1.title"),
      description: t("gallery.photos.mrf1.description"),
      image: "/images/gallery/mrf1.jpeg",
      category: "Activity",
      alt: "Ministry of Education Afghanistan violence against women event 2019",
      width: 800,
      height: 600,
    },
    {
      id: 13,
      title: t("gallery.photos.mrf2.title"),
      description: t("gallery.photos.mrf2.description"),
      image: "/images/gallery/mrf2.jpeg",
      category: "Activity",
      alt: "Government officials elimination of violence against women 2019",
      width: 800,
      height: 600,
    },
    {
      id: 14,
      title: t("gallery.photos.islm1.title"),
      description: t("gallery.photos.islm1.description"),
      image: "/images/gallery/islm1.jpeg",
      category: "Activity",
      alt: "Protest anniversary collapse of Afghanistan Islamabad 2024",
      width: 800,
      height: 600,
    },
    {
      id: 15,
      title: t("gallery.photos.islm2.title"),
      description: t("gallery.photos.islm2.description"),
      image: "/images/gallery/islm2.jpeg",
      category: "Activity",
      alt: "PFAM protest Taliban regime Pakistan 2024 - Afghan resistance",
      width: 800,
      height: 600,
    },
    {
      id: 16,
      title: t("gallery.photos.prot.title"),
      description: t("gallery.photos.prot.description"),
      image: "/images/gallery/prot1.jpeg",
      category: "Activity",
      alt: "Taliban opponents protest 2024 - Afghanistan democracy movement",
      width: 800,
      height: 600,
    },
    {
      id: 17,
      title: t("gallery.photos.glb1.title"),
      description: t("gallery.photos.glb1.description"),
      image: "/images/gallery/glb1.jpeg",
      category: "Activity",
      alt: "Global Call Save Afghan Women Girls protest Islamabad 2023",
      width: 800,
      height: 600,
    },
    {
      id: 18,
      title: t("gallery.photos.glb2.title"),
      description: t("gallery.photos.glb2.description"),
      image: "/images/gallery/glb2.jpeg",
      category: "Activity",
      alt: "International protest for Afghan women rights Pakistan 2023",
      width: 800,
      height: 600,
    },
    {
      id: 19,
      title: t("gallery.photos.irr1.title"),
      description: t("gallery.photos.irr1.description"),
      image: "/images/gallery/irr1.jpeg",
      category: "Activity",
      alt: "PFAM movement members protest Tehran Iran 2025",
      width: 800,
      height: 600,
    },
    {
      id: 20,
      title: t("gallery.photos.irr2.title"),
      description: t("gallery.photos.irr2.description"),
      image: "/images/gallery/irr2.jpeg",
      category: "Activity",
      alt: "Afghan activists protest Iran 2025 - Women rights demonstration",
      width: 800,
      height: 600,
    },
    {
      id: 21,
      title: t("gallery.photos.irr3.title"),
      description: t("gallery.photos.irr3.description"),
      image: "/images/gallery/irr3.jpeg",
      category: "Activity",
      alt: "Progressive Forces Afghanistan Movement Tehran protest 2025",
      width: 800,
      height: 600,
    },
    {
      id: 22,
      title: t("gallery.photos.hmb1.title"),
      description: t("gallery.photos.hmb1.description"),
      image: "/images/gallery/hmb1.jpeg",
      category: "Activity",
      alt: "Fourth anniversary collapse Afghanistan protest Hamburg Germany 2025",
      width: 800,
      height: 600,
    },
    {
      id: 23,
      title: t("gallery.photos.geneva.title"),
      description: t("gallery.photos.geneva.description"),
      image: "/images/gallery/geneva.jpeg",
      category: "Activity",
      alt: "United Nations headquarters Geneva PFAM delegation 2024",
      width: 800,
      height: 600,
    },
    {
      id: 24,
      title: t("gallery.photos.prt1.title"),
      description: t("gallery.photos.prt1.description"),
      image: "/images/gallery/prt1.jpeg",
      category: "Activity",
      alt: "Protest fourth anniversary Afghanistan surrender Pakistan 2024",
      width: 800,
      height: 600,
    },
    {
      id: 25,
      title: t("gallery.photos.off1.title"),
      description: t("gallery.photos.off1.description"),
      image: "/images/gallery/off1.jpeg",
      category: "Activity",
      alt: "Public Protection Directorate office Kabul Afghanistan 2020",
      width: 800,
      height: 600,
    },
    {
      id: 26,
      title: t("gallery.photos.adv1.title"),
      description: t("gallery.photos.adv1.description"),
      image: "/images/gallery/adv1.jpeg",
      category: "Activity",
      alt: "Pakistan advocacy program PFAM Islamabad 2022",
      width: 800,
      height: 600,
    },
    {
      id: 27,
      title: t("gallery.photos.adv2.title"),
      description: t("gallery.photos.adv2.description"),
      image: "/images/gallery/adv2.jpeg",
      category: "Activity",
      alt: "Afghan Civil Society Organization appreciation event 2024",
      width: 800,
      height: 600,
    },
    {
      id: 28,
      title: t("gallery.photos.three1.title"),
      description: t("gallery.photos.three1.description"),
      image: "/images/gallery/three1.jpeg",
      category: "Activity",
      alt: "Protest three years Taliban rule Islamabad 2022",
      width: 800,
      height: 600,
    },
    {
      id: 29,
      title: t("gallery.photos.islam1.title"),
      description: t("gallery.photos.islam1.description"),
      image: "/images/gallery/islam1.jpeg",
      category: "Activity",
      alt: "Mass protest against Taliban Islamabad 2022",
      width: 800,
      height: 600,
    },
    {
      id: 30,
      title: t("gallery.photos.dakh1.title"),
      description: t("gallery.photos.dakh1.description"),
      image: "/images/gallery/dakh1.jpeg",
      category: "Activity",
      alt: "Ministry of Interior Afghanistan gender policy meeting 2018",
      width: 800,
      height: 600,
    },
    {
      id: 31,
      title: t("gallery.photos.adova1.title"),
      description: t("gallery.photos.adova1.description"),
      image: "/images/gallery/adova1.jpeg",
      category: "Activity",
      alt: "Advocates for women rights in Afghanistan Pakistan 2024",
      width: 800,
      height: 600,
    },
    {
      id: 32,
      title: t("gallery.photos.prot2.title"),
      description: t("gallery.photos.prot2.description"),
      image: "/images/gallery/prot2.jpeg",
      category: "Activity",
      alt: "Taliban opponents protest demonstration 2024",
      width: 800,
      height: 600,
    },
    {
      id: 33,
      title: t("gallery.photos.prot3.title"),
      description: t("gallery.photos.prot3.description"),
      image: "/images/gallery/prot3.jpeg",
      category: "Activity",
      alt: "Anti-Taliban protest activists Pakistan 2024",
      width: 800,
      height: 600,
    },
    {
      id: 34,
      title: t("gallery.photos.apa1.title"),
      description: t("gallery.photos.apa1.description"),
      image: "/images/gallery/apa1.jpeg",
      category: "Activity",
      alt: "Mass protest against gender apartheid Afghanistan Islamabad 2022",
      width: 800,
      height: 600,
    },
    {
      id: 35,
      title: t("gallery.photos.prot4.title"),
      description: t("gallery.photos.prot4.description"),
      image: "/images/gallery/prot4.jpeg",
      category: "Activity",
      alt: "Protest against Taliban regime Islamabad 2024",
      width: 800,
      height: 600,
    },
    {
      id: 36,
      title: t("gallery.photos.conf1.title"),
      description: t("gallery.photos.conf1.description"),
      image: "/images/gallery/conf1.jpeg",
      category: "Activity",
      alt: "German Immigration Office conference Afghanistan 2025",
      width: 800,
      height: 600,
    },
    {
      id: 37,
      title: t("gallery.photos.conf1.title"),
      description: t("gallery.photos.conf1.description"),
      image: "/images/gallery/conf2.jpeg",
      category: "Activity",
      alt: "Church charity organizations conference Wolfsburg 2025",
      width: 800,
      height: 600,
    },
    {
      id: 38,
      title: t("gallery.photos.conf1.title"),
      description: t("gallery.photos.conf1.description"),
      image: "/images/gallery/conf3.jpeg",
      category: "Activity",
      alt: "Migration policies conference Germany 2025",
      width: 800,
      height: 600,
    },
    {
      id: 39,
      title: t("gallery.photos.conf1.title"),
      description: t("gallery.photos.conf1.description"),
      image: "/images/gallery/conf4.jpeg",
      category: "Activity",
      alt: "Afghan refugee situation conference 2025",
      width: 800,
      height: 600,
    },
    {
      id: 40,
      title: t("gallery.photos.conf1.title"),
      description: t("gallery.photos.conf1.description"),
      image: "/images/gallery/conf5.jpeg",
      category: "Activity",
      alt: "International migration conference Germany 2025",
      width: 800,
      height: 600,
    },
    {
      id: 41,
      title: t("gallery.photos.massoud1.title"),
      description: t("gallery.photos.massoud1.description"),
      image: "/images/gallery/massoud1.jpeg",
      category: "Activity",
      alt: "Ahmad Shah Massoud conference Hamburg Germany 2025",
      width: 800,
      height: 600,
    },
    {
      id: 42,
      title: t("gallery.photos.massoud1.title"),
      description: t("gallery.photos.massoud1.description"),
      image: "/images/gallery/massoud2.jpeg",
      category: "Activity",
      alt: "National hero Ahmad Shah Massoud legacy conference 2025",
      width: 800,
      height: 600,
    },
  ];

  // Video data with fixed duplicate IDs and better thumbnails
  const videoData = [
    {
      id: 51,
      title: t("gallery.videos.progressive_forces_statement_tehran.title"),
      description: t("gallery.videos.progressive_forces_statement_tehran.description"),
      youtubeId: "FITRm3BkYus",
      thumbnail: "https://img.youtube.com/vi/FITRm3BkYus/hqdefault.jpg",
    },
    {
      id: 52,
      title: t("gallery.videos.protest_day3_berlin.title"),
      description: t("gallery.videos.protest_day3_berlin.description"),
      youtubeId: "uRhru_V4EkA",
      thumbnail: "https://img.youtube.com/vi/uRhru_V4EkA/hqdefault.jpg",
    },
    {
      id: 51,
      title: t("gallery.videos.protest_day3_berlin.title"),
      description: t("gallery.videos.protest_day3_berlin.description"),
      youtubeId: "a1LVUMhb4vg",
      thumbnail: "https://img.youtube.com/vi/a1LVUMhb4vg/hqdefault.jpg",
    },
    {
      id: 50,
      title: t("gallery.videos.fatima_farahmand_15agust_statement.title"),
      description: t("gallery.videos.fatima_farahmand_15agust_statement.description"),
      youtubeId: "noWgARKo8qo",
      thumbnail: "https://img.youtube.com/vi/noWgARKo8qo/hqdefault.jpg",
    },
    {
      id: 49,
      title: t("gallery.videos.arifa_ahmadi_15agust_statement.title"),
      description: t("gallery.videos.arifa_ahmadi_15agust_statement.description"),
      youtubeId: "pknjIAhe1rw",
      thumbnail: "https://img.youtube.com/vi/pknjIAhe1rw/hqdefault.jpg",
    },
    {
      id: 48,
      title: t("gallery.videos.homaira_ebrahim_15agust_statement.title"),
      description: t("gallery.videos.homaira_ebrahim_15agust_statement.description"),
      youtubeId: "Oui96Kw5WmY",
      thumbnail: "https://img.youtube.com/vi/Oui96Kw5WmY/hqdefault.jpg",
    },
    {
      id: 47,
      title: t("gallery.videos.molouda_tawana_15agust_statement.title"),
      description: t("gallery.videos.molouda_tawana_15agust_statement.description"),
      youtubeId: "yvNj8IKvz30",
      thumbnail: "https://img.youtube.com/vi/yvNj8IKvz30/hqdefault.jpg",
    },
    {
      id: 46,
      title: t("gallery.videos.arifa_ahmadi_girls_education_ban.title"),
      description: t(
        "gallery.videos.arifa_ahmadi_girls_education_ban.description",
      ),
      youtubeId: "EmBD_fuIxNY",
      thumbnail: "https://img.youtube.com/vi/EmBD_fuIxNY/hqdefault.jpg",
    },
    {
      id: 45,
      title: t(
        "gallery.videos.mawloda_tawana_icc_taliban_arrest_warrants.title",
      ),
      description: t(
        "gallery.videos.mawloda_tawana_icc_taliban_arrest_warrants.description",
      ),
      youtubeId: "XBGDSxpLauY",
      thumbnail: "https://img.youtube.com/vi/XBGDSxpLauY/hqdefault.jpg",
    },
    {
      id: 44,
      title: t("gallery.videos.mawloda_tawana_gender_apartheid.title"),
      description: t(
        "gallery.videos.mawloda_tawana_gender_apartheid.description",
      ),
      youtubeId: "eVPIy6wf-YY",
      thumbnail: "https://img.youtube.com/vi/eVPIy6wf-YY/hqdefault.jpg",
    },
    {
      id: 43,
      title: t("gallery.photos.neshast_zanan.title"),
      description: t("gallery.photos.neshast_zanan.description"),
      youtubeId: "4T_CVHXEgGQ",
      thumbnail: "https://img.youtube.com/vi/4T_CVHXEgGQ/hqdefault.jpg",
    },
    {
      id: 42,
      title: t("gallery.videos.tawana_herat_suppression.title"),
      description: t("gallery.videos.tawana_herat_suppression.description"),
      youtubeId: "F9J-dF0iOV4",
      thumbnail: "https://img.youtube.com/vi/F9J-dF0iOV4/hqdefault.jpg",
    },
    {
      id: 41,
      title: t("gallery.videos.farahmand_herat_suppression.title"),
      description: t("gallery.videos.farahmand_herat_suppression.description"),
      youtubeId: "qas0Kg4msjQ",
      thumbnail: "https://img.youtube.com/vi/qas0Kg4msjQ/hqdefault.jpg",
    },
    {
      id: 40,
      title: t("gallery.videos.humaira_herat_suppression.title"),
      description: t("gallery.videos.humaira_herat_suppression.description"),
      youtubeId: "qcd_03AvIl0",
      thumbnail: "https://img.youtube.com/vi/qcd_03AvIl0/hqdefault.jpg",
    },
    {
      id: 39,
      title: t("gallery.videos.hamburg_protest_herat_2026.title"),
      description: t("gallery.videos.hamburg_protest_herat_2026.description"),
      youtubeId: "get5n9nZ_cI",
      thumbnail: "https://img.youtube.com/vi/get5n9nZ_cI/hqdefault.jpg",
    },
    {
      id: 38,
      title: t("gallery.videos.hamburg_protest_herat_2026.title"),
      description: t("gallery.videos.hamburg_protest_herat_2026.description"),
      youtubeId: "vw14GMDmr8s",
      thumbnail: "https://img.youtube.com/vi/vw14GMDmr8s/hqdefault.jpg",
    },
    {
      id: 37,
      title: t("gallery.videos.fawzia_koofi_hearus.title"),
      description: t("gallery.videos.fawzia_koofi_hearus.description"),
      youtubeId: "4ad04-DXS_Q",
      thumbnail: "https://img.youtube.com/vi/4ad04-DXS_Q/hqdefault.jpg",
    },
    // Video 1
    {
      id: 35,
      title: t("gallery.videos.solidarity_afghan_women.title"),
      description: t("gallery.videos.solidarity_afghan_women.description"),
      youtubeId: "00ZMpbsRB4k",
      thumbnail: "https://img.youtube.com/vi/00ZMpbsRB4k/hqdefault.jpg",
    },
    {
      id: 36,
      title: t("gallery.videos.spain_foreign_ministry_statement.title"),
      description: t(
        "gallery.videos.spain_foreign_ministry_statement.description",
      ),
      youtubeId: "War_lVa4Cho",
      thumbnail: "https://img.youtube.com/vi/War_lVa4Cho/hqdefault.jpg",
    },
    {
      id: 29,
      title: t("gallery.videos.fatima_farahmand.title"),
      description: t("gallery.videos.fatima_farahmand.description"),
      youtubeId: "nHjvr5f3p_c",
      thumbnail: "https://img.youtube.com/vi/nHjvr5f3p_c/hqdefault.jpg",
    },

    // Video 2
    {
      id: 30,
      title: t("gallery.videos.maryam_noori.title"),
      description: t("gallery.videos.maryam_noori.description"),
      youtubeId: "Zrm5uUrjly4",
      thumbnail: "https://img.youtube.com/vi/Zrm5uUrjly4/hqdefault.jpg",
    },

    // Video 3
    {
      id: 31,
      title: t("gallery.videos.humaira_ibrahim.title"),
      description: t("gallery.videos.humaira_ibrahim.description"),
      youtubeId: "i4GAXqxIfug",
      thumbnail: "https://img.youtube.com/vi/i4GAXqxIfug/hqdefault.jpg",
    },

    // Video 4
    {
      id: 32,
      title: t("gallery.videos.freshta_naseri.title"),
      description: t("gallery.videos.freshta_naseri.description"),
      youtubeId: "eg3LX-tEH8Y",
      thumbnail: "https://img.youtube.com/vi/eg3LX-tEH8Y/hqdefault.jpg",
    },

    // Video 5

    // Video 6
    {
      id: 34,
      title: t("gallery.videos.mawoloda_tawana.title"),
      description: t("gallery.videos.mawoloda_tawana.description"),
      youtubeId: "8Qw0qNwwVyg",
      thumbnail: "https://img.youtube.com/vi/8Qw0qNwwVyg/hqdefault.jpg",
    },
    {
      id: 29,
      title: t("gallery.videos.quadripartite_video1.title"),
      description: t("gallery.videos.quadripartite_video1.description"),
      youtubeId: "niiWHqhbdFA",
      thumbnail: "https://img.youtube.com/vi/niiWHqhbdFA/hqdefault.jpg",
    },
    {
      id: 28,
      title: t("gallery.videos.quadripartite_video1.title"),
      description: t("gallery.videos.quadripartite_video1.description"),
      youtubeId: "IpNKXH2-WBE",
      thumbnail: "https://img.youtube.com/vi/IpNKXH2-WBE/hqdefault.jpg",
    },
    {
      id: 27,
      title: t("gallery.videos.video19.title"),
      description: t("gallery.videos.video19.description"),
      youtubeId: "BN41r6Ju5Q4",
      thumbnail: "https://img.youtube.com/vi/BN41r6Ju5Q4/hqdefault.jpg",
    },
    {
      id: 26,
      title: t("gallery.videos.video19.title"),
      description: t("gallery.videos.video19.description"),
      youtubeId: "uvBdbpvKCLA",
      thumbnail: "https://img.youtube.com/vi/uvBdbpvKCLA/hqdefault.jpg",
    },
    {
      id: 25,
      title: t("gallery.videos.video19.title"),
      description: t("gallery.videos.video19.description"),
      youtubeId: "O911tLgrVUo",
      thumbnail: "https://img.youtube.com/vi/O911tLgrVUo/hqdefault.jpg",
    },
    {
      id: 19,
      title: t("gallery.videos.video19.title"),
      description: t("gallery.videos.video19.description"),
      youtubeId: "MZd6NioTnoM",
      thumbnail: "https://img.youtube.com/vi/MZd6NioTnoM/hqdefault.jpg",
    },
    {
      id: 20,
      title: t("gallery.videos.video20.title"),
      description: t("gallery.videos.video20.description"),
      youtubeId: "9n0ROsMnmf0",
      thumbnail: "https://img.youtube.com/vi/9n0ROsMnmf0/hqdefault.jpg",
    },
    {
      id: 21,
      title: t("gallery.videos.video19.title"),
      description: t("gallery.videos.video19.description"),
      youtubeId: "HFy4-Mtm1aU",
      thumbnail: "https://img.youtube.com/vi/HFy4-Mtm1aU/hqdefault.jpg",
    },
    {
      id: 22,
      title: t("gallery.videos.video19.title"),
      description: t("gallery.videos.video19.description"),
      youtubeId: "Kxl3ypNCvM4",
      thumbnail: "https://img.youtube.com/vi/Kxl3ypNCvM4/hqdefault.jpg",
    },
    {
      id: 23,
      title: t("gallery.videos.video19.title"),
      description: t("gallery.videos.video19.description"),
      youtubeId: "s0dgOYlA_M8",
      thumbnail: "https://img.youtube.com/vi/s0dgOYlA_M8/hqdefault.jpg",
    },
    {
      id: 24,
      title: t("gallery.videos.video20.title"),
      description: t("gallery.videos.video20.description"),
      youtubeId: "OIN9A2-VTNU",
      thumbnail: "https://img.youtube.com/vi/OIN9A2-VTNU/hqdefault.jpg",
    },
    {
      id: 17,
      title: t("gallery.videos.video17.title"),
      description: t("gallery.videos.video17.description"),
      youtubeId: "vxtf8_uZzNk",
      thumbnail: "https://img.youtube.com/vi/vxtf8_uZzNk/hqdefault.jpg",
    },
    {
      id: 18,
      title: t("gallery.videos.video18.title"),
      description: t("gallery.videos.video18.description"),
      youtubeId: "cSmTFO5jFUs",
      thumbnail: "https://img.youtube.com/vi/cSmTFO5jFUs/hqdefault.jpg",
    },
    {
      id: 16,
      title: t("gallery.videos.video16.title"),
      description: t("gallery.videos.video16.description"),
      youtubeId: "d9qs8lNBntE",
      thumbnail: "https://img.youtube.com/vi/d9qs8lNBntE/hqdefault.jpg",
    },
    {
      id: 7,
      title: t("gallery.videos.video7.title"),
      description: t("gallery.videos.video7.description"),
      youtubeId: "twaqrfYEf_w",
      thumbnail: "https://img.youtube.com/vi/twaqrfYEf_w/hqdefault.jpg",
    },
    {
      id: 8,
      title: t("gallery.videos.video8.title"),
      description: t("gallery.videos.video8.description"),
      youtubeId: "wzLXS6WQ8Qw",
      thumbnail: "https://img.youtube.com/vi/wzLXS6WQ8Qw/hqdefault.jpg",
    },
    {
      id: 9,
      title: t("gallery.videos.video9.title"),
      description: t("gallery.videos.video9.description"),
      youtubeId: "2QeEtxbIl2c",
      thumbnail: "https://img.youtube.com/vi/2QeEtxbIl2c/hqdefault.jpg",
    },
    {
      id: 10,
      title: t("gallery.videos.video10.title"),
      description: t("gallery.videos.video10.description"),
      youtubeId: "X9PLMgvNxNs",
      thumbnail: "https://img.youtube.com/vi/X9PLMgvNxNs/hqdefault.jpg",
    },
    {
      id: 11,
      title: t("gallery.videos.video11.title"),
      description: t("gallery.videos.video11.description"),
      youtubeId: "6ma2wK6cVes",
      thumbnail: "https://img.youtube.com/vi/6ma2wK6cVes/hqdefault.jpg",
    },
    {
      id: 12,
      title: t("gallery.videos.video12.title"),
      description: t("gallery.videos.video12.description"),
      youtubeId: "R_9voGlOMk8",
      thumbnail: "https://img.youtube.com/vi/R_9voGlOMk8/hqdefault.jpg",
    },
    {
      id: 13,
      title: t("gallery.videos.video13.title"),
      description: t("gallery.videos.video13.description"),
      youtubeId: "xA-tBWWnV-o",
      thumbnail: "https://img.youtube.com/vi/xA-tBWWnV-o/hqdefault.jpg",
    },
    {
      id: 14,
      title: t("gallery.videos.video14.title"),
      description: t("gallery.videos.video14.description"),
      youtubeId: "xP4nZ8Dfr6o",
      thumbnail: "https://img.youtube.com/vi/xP4nZ8Dfr6o/hqdefault.jpg",
    },
    {
      id: 15,
      title: t("gallery.videos.video15.title"),
      description: t("gallery.videos.video15.description"),
      youtubeId: "6p_0ru23SZM",
      thumbnail: "https://img.youtube.com/vi/6p_0ru23SZM/hqdefault.jpg",
    },
    {
      id: 1,
      title: t("gallery.videos.video1.title"),
      description: t("gallery.videos.video1.description"),
      youtubeId: "zG2-bjfVVFE",
      thumbnail: "https://img.youtube.com/vi/zG2-bjfVVFE/hqdefault.jpg",
    },
    {
      id: 2,
      title: t("gallery.videos.video2.title"),
      description: t("gallery.videos.video2.description"),
      youtubeId: "qou9ZRuGlHQ",
      thumbnail: "https://img.youtube.com/vi/qou9ZRuGlHQ/hqdefault.jpg",
    },
    {
      id: 3,
      title: t("gallery.videos.video3.title"),
      description: t("gallery.videos.video3.description"),
      youtubeId: "myfiT9zb1h0",
      thumbnail: "https://img.youtube.com/vi/myfiT9zb1h0/hqdefault.jpg",
    },
    {
      id: 4,
      title: t("gallery.videos.video4.title"),
      description: t("gallery.videos.video4.description"),
      youtubeId: "joHXE-e2oWw",
      thumbnail: "https://img.youtube.com/vi/joHXE-e2oWw/hqdefault.jpg",
    },
    {
      id: 5,
      title: t("gallery.videos.video5.title"),
      description: t("gallery.videos.video5.description"),
      youtubeId: "Z4Ph8JhZqvk",
      thumbnail: "https://img.youtube.com/vi/Z4Ph8JhZqvk/hqdefault.jpg",
    },
    {
      id: 6,
      title: t("gallery.videos.video6.title"),
      description: t("gallery.videos.video6.description"),
      youtubeId: "Oe1X4udU3vk",
      thumbnail: "https://img.youtube.com/vi/Oe1X4udU3vk/hqdefault.jpg",
    },
  ];

  // Featured photos for the slideshow
  const featuredPhotos = photoData.slice(0, 5);

  // Image loading handler
  const handleImageLoad = (imageId) => {
    setImagesLoaded((prev) => ({ ...prev, [imageId]: true }));
  };

  // Initialize slideshow
  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredPhotos.length);
    }, 7000);

    return () => clearInterval(slideInterval);
  }, [featuredPhotos.length]);

  // SEO Optimization with enhanced Image Schema
  useEffect(() => {
    // Update document title
    document.title =
      "PFA Movement Gallery - Women's Rights in Afghanistan | Progressive Forces";

    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.name = "description";
      document.head.appendChild(metaDescription);
    }
    metaDescription.content =
      "Explore PFA Movement's photo and video gallery showcasing protests, events, and activism for women's rights and gender equality in Afghanistan. See our fight against Taliban oppression.";

    // Update or create Open Graph tags
    const updateMetaTag = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    };

    // Open Graph tags
    updateMetaTag(
      "og:title",
      "PFA Movement Gallery - Women's Rights Activism in Afghanistan",
    );
    updateMetaTag(
      "og:description",
      "Photo and video documentation of Progressive Forces of Afghanistan Movement's fight for women's rights, education, and gender equality against Taliban oppression.",
    );
    updateMetaTag("og:image", "/images/gallery/og-image.jpg");
    updateMetaTag("og:url", window.location.href);
    updateMetaTag("og:type", "website");

    // Twitter Card tags
    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag(
      "twitter:title",
      "PFA Movement Gallery - Afghanistan Women's Rights",
    );
    updateMetaTag(
      "twitter:description",
      "Documenting the fight for gender equality and women's education in Afghanistan through photos and videos.",
    );
    updateMetaTag("twitter:image", "/images/gallery/og-image.jpg");

    // Add Image Schema structured data to document head
    const imageScriptId = "image-structured-data";
    let existingImageScript = document.getElementById(imageScriptId);
    if (existingImageScript) {
      existingImageScript.remove();
    }

    const imageScript = document.createElement("script");
    imageScript.id = imageScriptId;
    imageScript.type = "application/ld+json";
    imageScript.textContent = JSON.stringify(generateImageStructuredData());
    document.head.appendChild(imageScript);

    // Add Video Schema structured data
    const videoScriptId = "video-structured-data";
    let existingVideoScript = document.getElementById(videoScriptId);
    if (existingVideoScript) {
      existingVideoScript.remove();
    }

    const videoScript = document.createElement("script");
    videoScript.id = videoScriptId;
    videoScript.type = "application/ld+json";
    videoScript.textContent = JSON.stringify(generateVideoStructuredData());
    document.head.appendChild(videoScript);

    return () => {
      // Clean up
      if (existingImageScript) {
        document.head.removeChild(existingImageScript);
      }
      if (existingVideoScript) {
        document.head.removeChild(existingVideoScript);
      }
    };
  }, []);

  // Animation on scroll for photo cards
  useEffect(() => {
    const animatedElements = document.querySelectorAll(
      ".photo-card, .video-card",
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate__fadeInUp");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 },
    );

    animatedElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      animatedElements.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, [currentPage]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredPhotos.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + featuredPhotos.length) % featuredPhotos.length,
    );
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Modal navigation functions
  const nextModalSlide = () => {
    if (selectedPhoto) {
      const currentIndex = photoData.findIndex(
        (photo) => photo.id === selectedPhoto.id,
      );
      const nextIndex = (currentIndex + 1) % photoData.length;
      setSelectedPhoto(photoData[nextIndex]);
      setModalCurrentSlide(nextIndex);
    }
  };

  const prevModalSlide = () => {
    if (selectedPhoto) {
      const currentIndex = photoData.findIndex(
        (photo) => photo.id === selectedPhoto.id,
      );
      const prevIndex =
        (currentIndex - 1 + photoData.length) % photoData.length;
      setSelectedPhoto(photoData[prevIndex]);
      setModalCurrentSlide(prevIndex);
    }
  };

  const handlePhotoClick = (photo) => {
    setSelectedPhoto(photo);
    setIsModalOpen(true);
    const photoIndex = photoData.findIndex((p) => p.id === photo.id);
    setModalCurrentSlide(photoIndex);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPhoto(null);
  };

  // Pagination logic
  const indexOfLastPhoto = currentPage * photosPerPage;
  const indexOfFirstPhoto = indexOfLastPhoto - photosPerPage;
  const currentPhotos = photoData.slice(indexOfFirstPhoto, indexOfLastPhoto);
  const totalPages = Math.ceil(photoData.length / photosPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Close modal on Escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.keyCode === 27) {
        closeModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen]);

  return (
    <div className="gallery-page">
      {/* Add JSON-LD for Image Gallery in the body as well for better SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateImageStructuredData()),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateVideoStructuredData()),
        }}
      />

      <Navbar />

      {/* Featured Slideshow Section with enhanced Image Schema */}
      {/* Featured Slideshow Section with enhanced Image Schema */}
      <section
        className="featured-slideshow-section"
        itemScope
        itemType="https://schema.org/ImageGallery"
      >
        <div className="container">
          <h1 className="page-title text-center mb-4" itemProp="name">
            {t("gallery.title")}
          </h1>
          <p className="page-subtitle text-center mb-5" itemProp="description">
            {t("gallery.subtitle")}
          </p>

          <div className="image-gallery">
            <div
              className="gallery-slides"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {featuredPhotos.map((photo, index) => (
                <div
                  key={index}
                  className="gallery-slide"
                  style={{ backgroundImage: `url(${photo.image})` }}
                  itemScope
                  itemProp="image"
                  itemType="https://schema.org/ImageObject"
                >
                  <meta itemProp="url" content={photo.image} />
                  <meta itemProp="name" content={photo.title} />
                  <meta itemProp="description" content={photo.description} />
                  <meta itemProp="caption" content={photo.title} />
                  <meta itemProp="width" content={photo.width.toString()} />
                  <meta itemProp="height" content={photo.height.toString()} />
                  <meta
                    itemProp="license"
                    content="https://creativecommons.org/licenses/by-nc/4.0/"
                  />
                  <meta
                    itemProp="acquireLicensePage"
                    content="https://pfa-movement.com/gallery"
                  />
                  <meta
                    itemProp="copyrightNotice"
                    content={`Copyright © ${new Date().getFullYear()} Progressive Forces of Afghanistan Movement`}
                  />
                  {/* حذف overlay از روی تصویر */}
                </div>
              ))}
            </div>
            <div className="gallery-nav gallery-prev" onClick={prevSlide}>
              <i className="fas fa-chevron-left"></i>
            </div>
            <div className="gallery-nav gallery-next" onClick={nextSlide}>
              <i className="fas fa-chevron-right"></i>
            </div>
            <div className="gallery-controls">
              {featuredPhotos.map((_, index) => (
                <div
                  key={index}
                  className={`gallery-control ${index === currentSlide ? "active" : ""}`}
                  onClick={() => goToSlide(index)}
                ></div>
              ))}
            </div>
          </div>

          {/* عنوان و توضیحات در پایین اسلایدشو */}
          <div className="slide-caption text-center mt-3">
            <h3 itemProp="name">{featuredPhotos[currentSlide]?.title}</h3>
            <p itemProp="description">{featuredPhotos[currentSlide]?.description}</p>
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section
        className="photo-gallery-section"
        itemScope
        itemType="https://schema.org/ImageGallery"
      >
        <div className="container">
          <h2 className="section-title" itemProp="name">
            {t("gallery.photoGallery")}
          </h2>

          <div className="row">
            {currentPhotos.map((photo) => (
              <div key={photo.id} className="col-lg-3 col-md-4 col-sm-6 mb-4">
                <div
                  className="photo-card bg-white shadow-sm animate__animated clickable-card"
                  onClick={() => handlePhotoClick(photo)}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-5px)";
                    e.currentTarget.style.transition = "transform 0.3s ease";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  itemScope
                  itemProp="image"
                  itemType="https://schema.org/ImageObject"
                >
                  <div className="card-img-container">
                    <img
                      src={photo.image}
                      alt={photo.alt}
                      itemProp="contentUrl"
                      loading="lazy"
                      width={photo.width}
                      height={photo.height}
                      onLoad={() => handleImageLoad(photo.id)}
                      className={imagesLoaded[photo.id] ? "loaded" : "loading"}
                    />
                    <meta itemProp="name" content={photo.title} />
                    <meta itemProp="description" content={photo.description} />
                    <meta itemProp="caption" content={photo.title} />
                    <meta itemProp="width" content={photo.width.toString()} />
                    <meta itemProp="height" content={photo.height.toString()} />
                    <meta
                      itemProp="license"
                      content="https://creativecommons.org/licenses/by-nc/4.0/"
                    />
                    <meta
                      itemProp="acquireLicensePage"
                      content="https://pfa-movement.com/gallery"
                    />
                    <meta
                      itemProp="copyrightNotice"
                      content={`Copyright © ${new Date().getFullYear()} Progressive Forces of Afghanistan Movement`}
                    />
                    <div className="photo-overlay">
                      <i className="fas fa-search-plus"></i>
                    </div>
                  </div>
                  <div className="card-content">
                    <h5
                      className="clickable-title"
                      style={{ color: "#007bff" }}
                      itemProp="name"
                    >
                      {photo.title}
                    </h5>
                    <p className="photo-description" itemProp="description">
                      {photo.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="pagination-container d-flex justify-content-center mt-5">
            <nav aria-label="Gallery pagination">
              <ul className="pagination">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    {t("gallery.pagination.previous")}
                  </button>
                </li>

                {[...Array(totalPages)].map((_, index) => (
                  <li
                    key={index}
                    className={`page-item ${currentPage === index + 1 ? "active" : ""
                      }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => paginate(index + 1)}
                      aria-label={`Page ${index + 1}`}
                      aria-current={
                        currentPage === index + 1 ? "page" : undefined
                      }
                    >
                      {index + 1}
                    </button>
                  </li>
                ))}

                <li
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""
                    }`}
                >
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    {t("gallery.pagination.next")}
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section
        className="video-section"
        itemScope
        itemType="https://schema.org/VideoGallery"
      >
        <div className="container">
          <h2 className="section-title" itemProp="name">
            {t("gallery.videos.title")}
          </h2>
          <p
            className="section-subtitle text-center mb-5"
            itemProp="description"
          >
            {t("gallery.videos.subtitle")}
          </p>

          <div className="row">
            {videoData.map((video) => (
              <div key={video.id} className="col-lg-6 mb-4">
                <div
                  className="video-card bg-white shadow-sm animate__animated"
                  itemScope
                  itemProp="video"
                  itemType="https://schema.org/VideoObject"
                >
                  <div className="video-thumbnail">
                    <img
                      src={video.thumbnail}
                      alt={`Video: ${video.title}`}
                      className="w-100"
                      loading="lazy"
                      itemProp="thumbnailUrl"
                    />
                    <meta itemProp="name" content={video.title} />
                    <meta itemProp="description" content={video.description} />
                    <meta
                      itemProp="contentUrl"
                      content={`https://www.youtube.com/watch?v=${video.youtubeId}`}
                    />
                    <meta
                      itemProp="embedUrl"
                      content={`https://www.youtube.com/embed/${video.youtubeId}`}
                    />
                    <meta itemProp="uploadDate" content="2023-01-01" />
                    <div className="play-button">
                      <i className="fas fa-play"></i>
                    </div>
                  </div>
                  <div className="video-content p-3">
                    <h5 itemProp="name">{video.title}</h5>
                    <p itemProp="description">{video.description}</p>
                    <a
                      href={`https://www.youtube.com/watch?v=${video.youtubeId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary btn-sm"
                      itemProp="url"
                    >
                      {t("gallery.videos.watchButton")}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="youtube-channel text-center mt-5">
            <h4>{t("gallery.videos.subscribeTitle")}</h4>
            <p className="mb-3">{t("gallery.videos.subscribeDescription")}</p>
            <a
              href="https://www.youtube.com/channel/UCZPxJeeIQqO-ZxT1IZY_0Kg"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-danger"
            >
              <i className="fab fa-youtube me-2"></i>{" "}
              {t("gallery.videos.visitChannel")}
            </a>
          </div>
        </div>
      </section>

      {/* Photo Modal */}
      {/* Photo Modal */}
      {isModalOpen && selectedPhoto && (
        <div className="photo-modal">
          <div className="modal-overlay" onClick={closeModal}></div>
          <div className="modal-content">
            <button
              className="modal-close"
              onClick={closeModal}
              aria-label="Close modal"
            >
              <i className="fas fa-times"></i>
            </button>

            <div className="modal-image-container">
              <img
                src={selectedPhoto.image}
                alt={selectedPhoto.alt}
                itemProp="contentUrl"
              />
              <meta itemProp="name" content={selectedPhoto.title} />
              <meta
                itemProp="description"
                content={selectedPhoto.description}
              />

              <button
                className="modal-nav modal-prev"
                onClick={prevModalSlide}
                aria-label="Previous photo"
              >
                <i className="fas fa-chevron-left"></i>
              </button>

              <button
                className="modal-nav modal-next"
                onClick={nextModalSlide}
                aria-label="Next photo"
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>

            {/* توضیحات با فونت کوچک و ایتالیک در پایین */}
            <div className="modal-info">
              <h3 itemProp="name">{selectedPhoto.title}</h3>
              <p className="modal-description" itemProp="description">
                {selectedPhoto.description}
              </p>
              <div className="photo-counter">
                {modalCurrentSlide + 1} / {photoData.length}
              </div>
            </div>
          </div>
        </div>
      )}

      <Contact />
      <Donate />
      <Footer />
    </div>
  );
};

export default Gallery;
